import React, { useState, useMemo } from "react";
import { motion } from "motion/react";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Eye,
  CheckCircle2,
  Clock,
  DollarSign,
  Mail,
  Calendar,
  FileText,
  Edit,
  CheckCircle,
} from "lucide-react";
import FeaturePermission from "../permissions/FeaturePermission";
import { POST_PAID_PERMISSIONS } from "../../utils/permissions";
import { canApproveRequest } from "../../utils/permissions";

const PostPaidRequestTable = ({ 
  data = [], 
  loading = false, 
  onViewRequest, 
  onApprove, 
  onEdit,
  permissions = null 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [statusFilter, setStatusFilter] = useState("all");
  const itemsPerPage = 10;

  // Filter and search data
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(
      (item) =>
        item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.user_slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.amount?.toString().includes(searchTerm)
      );
    }

    // Ensure latest entries appear first (sort by created_at desc)
    filtered.sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });

    // Apply status filter
    if (statusFilter !== "all") {
      if (statusFilter === "pending") {
        filtered = filtered.filter((item) => item.status === "pending");
      } else if (statusFilter === "approved") {
        filtered = filtered.filter(
          (item) => item.hod_approval && item.finance_approval
        );
      } else if (statusFilter === "hod_approved") {
        filtered = filtered.filter(
          (item) => item.hod_approval && !item.finance_approval
        );
      } else if (statusFilter === "finance_approved") {
        filtered = filtered.filter(
          (item) => !item.hod_approval && item.finance_approval
        );
      }
    }

    return filtered;
  }, [data, searchTerm, statusFilter]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (sortConfig.key === "amount") {
        return sortConfig.direction === "asc"
          ? parseFloat(aValue) - parseFloat(bValue)
          : parseFloat(bValue) - parseFloat(aValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Get status badge
  const getStatusBadge = (request) => {
    if (request.hod_approval && request.finance_approval) {
      return (
        <span className="inline-flex gap-1 items-center px-3 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
          <CheckCircle2 className="w-3 h-3" />
          Fully Approved
        </span>
      );
    } else if (request.hod_approval && !request.finance_approval) {
      return (
        <span className="inline-flex gap-1 items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
          <Clock className="w-3 h-3" />
          Pending Finance Approval
        </span>
      );
    } else {
      return (
        <span className="inline-flex gap-1 items-center px-3 py-1 text-xs font-medium text-orange-800 bg-orange-100 rounded-full">
          <Clock className="w-3 h-3" />
          Pending HOD Approval
        </span>
      );
    }
  };

  // Get approval tier
  const getApprovalTier = (request) => {
    if (!request.hod_approval && !request.finance_approval) {
      return "hod"; // First tier - HOD needs to approve
    } else if (request.hod_approval && !request.finance_approval) {
      return "finance"; // Second tier - Finance needs to approve
    }
    return "completed"; // Both approved
  };

  // Check if request can be edited
  const canEdit = (request) => {
    return !request.hod_approval && !request.finance_approval;
  };

  return (
    <div className="p-8">
      {/* Controls */}
      <div className="flex flex-col gap-4 justify-between items-center mb-6 lg:flex-row">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, email, or amount..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="py-3 pr-4 pl-12 w-full rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="hod_approved">HOD Approved</option>
          <option value="finance_approved">Finance Approved</option>
          <option value="approved">Fully Approved</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border border-gray-200 transition-colors cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("customer_name")}
              >
                <div className="flex gap-2 items-center">
                  Customer
                  {sortConfig.key === "customer_name" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border border-gray-200 transition-colors cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("amount")}
              >
                <div className="flex gap-2 items-center">
                  Amount
                  {sortConfig.key === "amount" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </div>
              </th>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border border-gray-200">
                SMS Units
              </th>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border border-gray-200">
                Status
              </th>
              <th
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border border-gray-200 transition-colors cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex gap-2 items-center">
                  Created At
                  {sortConfig.key === "created_at" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </div>
              </th>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border border-gray-200">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center border border-gray-200"
                >
                  <div className="flex justify-center items-center">
                    <div className="w-8 h-8 rounded-full border-b-2 border-red-600 animate-spin"></div>
                    <span className="ml-2 text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center border border-gray-200"
                >
                  <div className="text-gray-500">
                    <FileText className="mx-auto mb-4 w-12 h-12 text-gray-300" />
                    <p className="text-lg font-medium">No requests found</p>
                    <p className="text-sm">
                      No post-paid requests match your criteria
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => (
                <motion.tr
                  key={row.user_slug + index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="transition-colors hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    <div className="flex gap-3 items-center">
                      <div className="flex-shrink-0">
                        <div className="flex justify-center items-center w-10 h-10 bg-red-600 rounded-full">
                          <span className="text-sm font-semibold text-white">
                            {row.customer_name?.charAt(0)?.toUpperCase() ||
                              row.customer_email?.charAt(0)?.toUpperCase() ||
                              "U"}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {row.customer_name || "Unknown"}
                        </p>
                        <p className="flex gap-1 items-center text-xs text-gray-500 truncate">
                          <Mail className="w-3 h-3" />
                          {row.customer_email}
                        </p>
                        <p className="text-xs font-medium text-red-600">
                          ID: {row.user_slug?.slice(0, 8)}...
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    <div className="flex gap-2 items-center">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-gray-900">
                        GH₵{parseFloat(row.amount || 0).toFixed(2)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      ₵{parseFloat(row.per_unit_price || 0).toFixed(4)} per unit
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    <span className="text-sm font-medium text-gray-900">
                      {row.sms_units?.toLocaleString() || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    {getStatusBadge(row)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    <div className="flex gap-2 items-center">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {row.created_at
                            ? new Date(row.created_at).toLocaleDateString()
                            : "Unknown"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {row.created_at
                            ? new Date(row.created_at).toLocaleTimeString()
                            : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    <div className="flex gap-2 items-center">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onViewRequest(row)}
                        className="p-2 text-red-600 rounded-lg transition-colors hover:bg-red-100"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                      
                      <FeaturePermission
                        permission={POST_PAID_PERMISSIONS.EDIT_REQUEST}
                        checkPermission={(admin) => {
                          if (!permissions?.canEdit) return false;
                          return canEdit(row);
                        }}
                      >
                        {canEdit(row) && onEdit && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onEdit(row)}
                            className="p-2 text-blue-600 rounded-lg transition-colors hover:bg-blue-100"
                            title="Edit Request"
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                        )}
                      </FeaturePermission>

                      <FeaturePermission
                        checkPermission={(admin) => {
                          if (getApprovalTier(row) === "completed") return false;
                          return canApproveRequest(admin, row);
                        }}
                      >
                        {getApprovalTier(row) !== "completed" && onApprove && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onApprove(row)}
                            className="p-2 text-green-600 rounded-lg transition-colors hover:bg-green-100"
                            title={`Approve ${getApprovalTier(row) === "hod" ? "HOD" : "Finance"}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </motion.button>
                        )}
                      </FeaturePermission>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>
            <div className="flex gap-2 items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </motion.button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <motion.button
                    key={page}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === page
                        ? "bg-red-600 text-white"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {page}
                  </motion.button>
                )
              )}

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostPaidRequestTable;

